import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { MessageMedium } from 'constants/messageMediums';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { TemplateQueryString, TemplateState } from '../types';
import { getQueryStringByKey } from '../context/utils';
import { getTemplateHasContent } from '../components/utils';

const TEMPLATE_QUERY_KEY = 'template';

const getIsDefaultTemplate = (search?: string) => {
  const state = getQueryStringByKey(TEMPLATE_QUERY_KEY, search);
  return state?.toLocaleLowerCase() === TemplateQueryString.Default.toLocaleLowerCase();
};

export const useTemplateState = (messageMedium: MessageMedium) => {
  const { isOpen } = useTemplateTakeover();
  const { allContentErrors, designContentErrors, designContentWarnings, nbaLocalesErrors } =
    useErrorValidationContext();

  const allContentErrorCount = allContentErrors.length;
  const templateHasContentIssues =
    !!allContentErrors.length || !!designContentErrors.length || !!designContentWarnings.length;

  // use the template as it will give us the latest, without loading the template through campaigns
  const { template, isLoading } = useTemplateQueryContext();

  const location = useLocation();
  const [isDefaultTemplate, setDefaultTemplate] = useState(() =>
    getIsDefaultTemplate(location?.search),
  );

  useEffect(() => {
    if (isOpen && isDefaultTemplate) {
      setDefaultTemplate(false);
    }
  }, [isOpen, isDefaultTemplate]);

  const templateAndContentParsed = JSON.parse(template);

  const noTemplate = !getTemplateHasContent(messageMedium, templateAndContentParsed?.mediumData);
  const isInvalid = !isLoading && !isDefaultTemplate && (allContentErrorCount > 0 || noTemplate); // invalid
  const isComplete =
    getTemplateHasContent(messageMedium, templateAndContentParsed?.mediumData) &&
    allContentErrorCount === 0; // complete

  const templateState: TemplateState = {
    noTemplate,
    isDefaultTemplate,
    isInvalid,
    isComplete,
    hasInvalidNbaLocales: nbaLocalesErrors.length > 0,
    errorCount: allContentErrorCount,
    templateHasContentIssues,
  };

  return {
    templateState,
  };
};
